import React, { useEffect, useMemo, useState } from 'react';
import { Customer } from '../../types';
import {
    LEAD_ACTIVITY_LABELS,
    Lead,
    LeadActivity,
    LeadActivityTyp,
    convertLeadToCustomer,
    createLeadActivity,
    fetchLeadActivities,
    setActivityDone,
    updateLead,
} from '../../services/leadService';
import {
    ActiveProjectPoint,
    LEAD_STATUS_LABELS,
    LeadStatus,
    allowedTransitions,
    computeLeadPriority,
    leadToCustomerDraft,
    zulaessigerErstkontakt,
} from '../../services/leadLogic';
import { scoreLead } from '../../services/leadScoring';
import { BRANCHE_OPTIONS, brancheLabel } from '../../services/branchen';
import { fetchCustomers } from '../../services/crmService';
import { DuplicateHit, findSimilarCustomers } from '../../services/customerDuplicates';
import { rechtFehlerOder, rechtFehltHinweis } from '../../services/rechte/rechtFehler';
import { Button } from '../Button';
import {
    AlertTriangle, ArrowLeft, Ban, Building2, CalendarClock, Check, CheckCircle2, Globe,
    Loader2, Mail, MapPin, Phone, Sparkles, User, UserPlus, X,
} from 'lucide-react';

/**
 * Lead-Detail: Stammdaten, Aktivitaeten-Timeline, Erstkontakt-Hinweis und die
 * drei Aktionen (Score berechnen, in Kunde umwandeln, verloren geben).
 *
 * Duenner Container — Uebergangspruefung, Priorisierung, Kanalwahl und die
 * Kunden-Vorbelegung stehen pur und getestet in leadLogic.ts, das Schreiben in
 * leadService.ts.
 */
interface Props {
    lead: Lead;
    activeProjects: ActiveProjectPoint[];
    onBack: () => void;
    /** Der Lead hat sich geaendert — der Aufrufer haelt die Liste aktuell. */
    onChange: (lead: Lead) => void;
    onStatusChange: (lead: Lead, to: LeadStatus) => void;
    /**
     * „In Kunde umwandeln“ legt einen Kunden an (`customers`, Kundennummer)
     * — das braucht „Kunden verwalten“ neben „Neukunden / Leads“
     * (Rollensystem, Nr. 152). Ohne Angabe wie bisher: ja.
     */
    darfKundeAnlegen?: boolean;
}

const KANAL_STIL: Record<string, string> = {
    telefon: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    brief: 'bg-amber-50 border-amber-200 text-amber-800',
    keiner: 'bg-red-50 border-red-200 text-red-800',
};

const datumDe = (iso?: string): string =>
    iso ? new Date(iso).toLocaleDateString('de-DE') : '';

const heuteIso = (): string => new Date().toLocaleDateString('sv-SE');

export const LeadDetailPanel: React.FC<Props> = ({
    lead, activeProjects, onBack, onChange, onStatusChange, darfKundeAnlegen = true,
}) => {
    const [activities, setActivities] = useState<LeadActivity[]>([]);
    const [ladeAktivitaeten, setLadeAktivitaeten] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [hinweis, setHinweis] = useState<string | null>(null);

    // Neue Aktivitaet
    const [typ, setTyp] = useState<LeadActivityTyp>('notiz');
    const [betreff, setBetreff] = useState('');
    const [inhalt, setInhalt] = useState('');
    const [faelligAm, setFaelligAm] = useState('');
    const [speichereAktivitaet, setSpeichereAktivitaet] = useState(false);

    // Aktionen
    const [scoring, setScoring] = useState(false);
    const [verlorenOffen, setVerlorenOffen] = useState(false);
    const [lostReason, setLostReason] = useState('');
    const [konvertierungOffen, setKonvertierungOffen] = useState(false);

    // Stammdaten-Bearbeitung
    const [bearbeiten, setBearbeiten] = useState(false);
    const [entwurf, setEntwurf] = useState<Partial<Lead>>({});
    const [speichere, setSpeichere] = useState(false);

    const prio = useMemo(() => computeLeadPriority(lead, activeProjects), [lead, activeProjects]);
    const erstkontakt = useMemo(() => zulaessigerErstkontakt(lead), [lead]);
    const ziele = allowedTransitions(lead.status, { customerId: lead.customerId });

    const ladeAktivitaetenNeu = async () => {
        setLadeAktivitaeten(true);
        try {
            setActivities(await fetchLeadActivities(lead.id));
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setLadeAktivitaeten(false);
        }
    };

    useEffect(() => {
        void ladeAktivitaetenNeu();
        setBearbeiten(false);
        setEntwurf({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lead.id]);

    // ─── Aktionen ────────────────────────────────────────────────────────────

    const handleScore = async () => {
        setScoring(true);
        setFehler(null);
        try {
            const ergebnis = await scoreLead({
                name: lead.name,
                branche: lead.branche,
                zip: lead.zip,
                city: lead.city,
                bundesland: lead.bundesland,
                website: lead.website,
                distanceKm: prio.distanceKm,
                nearestProjectName: undefined,
                projekteImUmkreis: activeProjects.length,
            });
            if (!ergebnis) {
                // Scoring ist optional (leadScoring.ts): kein Fehlerdialog,
                // nur ein Hinweis. Die Ursache steht auf der Konsole.
                setHinweis('Die KI-Bewertung ist derzeit nicht verfügbar — der Lead bleibt unbewertet.');
                return;
            }
            await updateLead(lead.id, { score: ergebnis.score, scoreBegruendung: ergebnis.begruendung });
            onChange({ ...lead, score: ergebnis.score, scoreBegruendung: ergebnis.begruendung });
            setHinweis(`Bewertung übernommen: ${ergebnis.score} von 100.`);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setScoring(false);
        }
    };

    const handleVerloren = () => {
        const grund = lostReason.trim();
        if (!grund) return;
        setVerlorenOffen(false);
        setLostReason('');
        onStatusChange({ ...lead, lostReason: grund }, 'verloren');
    };

    const handleAktivitaet = async () => {
        if (!betreff.trim() && !inhalt.trim()) return;
        setSpeichereAktivitaet(true);
        setFehler(null);
        try {
            await createLeadActivity({
                leadId: lead.id,
                typ,
                betreff: betreff.trim() || undefined,
                inhalt: inhalt.trim() || undefined,
                faelligAm: faelligAm || undefined,
            });
            // Wiedervorlage-Datum zusaetzlich am Lead fuehren: die Listen- und
            // Board-Ansicht liest next_action_at, ohne die Aktivitaeten zu
            // laden.
            if (faelligAm) {
                await updateLead(lead.id, { nextActionAt: faelligAm });
                onChange({ ...lead, nextActionAt: faelligAm });
            }
            setBetreff('');
            setInhalt('');
            setFaelligAm('');
            setTyp('notiz');
            await ladeAktivitaetenNeu();
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setSpeichereAktivitaet(false);
        }
    };

    const handleErledigt = async (a: LeadActivity) => {
        try {
            await setActivityDone(a.id, !a.erledigt);
            setActivities((prev) => prev.map((x) => (x.id === a.id ? { ...x, erledigt: !x.erledigt } : x)));
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        }
    };

    const handleSpeichern = async () => {
        setSpeichere(true);
        setFehler(null);
        try {
            await updateLead(lead.id, entwurf);
            onChange({ ...lead, ...entwurf });
            setBearbeiten(false);
            setEntwurf({});
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setSpeichere(false);
        }
    };

    const feld = (label: string, key: keyof Lead, icon?: React.ReactNode) => {
        const wert = (entwurf[key] ?? lead[key] ?? '') as string;
        return (
            <div>
                <label htmlFor={`lf-${key}`} className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                    {label}
                </label>
                {bearbeiten ? (
                    <input
                        id={`lf-${key}`}
                        value={wert}
                        onChange={(e) => setEntwurf({ ...entwurf, [key]: e.target.value })}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                ) : (
                    <p className="text-sm text-slate-700 flex items-center gap-1.5 min-h-[38px]">
                        {icon}
                        {wert || <span className="text-slate-300">—</span>}
                    </p>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-5">
            {/* Kopf */}
            <div data-glass="kopf" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                        <button
                            onClick={onBack}
                            className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 shrink-0"
                            title="Zurück zur Übersicht"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div className="min-w-0">
                            <h2 className="text-lg font-bold text-slate-800 truncate">{lead.name || 'Unbenannt'}</h2>
                            <p className="text-xs text-slate-500">
                                {brancheLabel(lead.branche)} · {LEAD_STATUS_LABELS[lead.status]} ·
                                <span title={prio.begruendung}> Priorität {prio.priority}</span>
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 justify-end">
                        <Button
                            variant="secondary"
                            onClick={() => void handleScore()}
                            disabled={scoring}
                            icon={scoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            title="Lead von der KI bewerten lassen (optional — bei Ausfall bleibt der Lead unbewertet)"
                        >
                            Score berechnen
                        </Button>
                        {!lead.customerId && (
                            <Button
                                variant="primary"
                                onClick={() => setKonvertierungOffen(true)}
                                disabled={!darfKundeAnlegen}
                                title={darfKundeAnlegen ? undefined : rechtFehltHinweis('kunden.verwalten')}
                                icon={<UserPlus className="w-4 h-4" />}
                            >
                                In Kunde umwandeln
                            </Button>
                        )}
                        {ziele.includes('verloren') && (
                            <Button
                                variant="secondary"
                                onClick={() => setVerlorenOffen(true)}
                                icon={<Ban className="w-4 h-4" />}
                            >
                                Verloren
                            </Button>
                        )}
                        {ziele.filter((z) => z !== 'verloren').map((z) => (
                            <Button key={z} variant="ghost" onClick={() => onStatusChange(lead, z)}>
                                → {LEAD_STATUS_LABELS[z]}
                            </Button>
                        ))}
                    </div>
                </div>

                {lead.customerId && (
                    <p className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 shrink-0" />
                        Dieser Lead wurde bereits in einen Kunden umgewandelt.
                    </p>
                )}
            </div>

            {hinweis && (
                <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-2xl p-3 text-sm flex items-start justify-between gap-3">
                    <span>{hinweis}</span>
                    <button onClick={() => setHinweis(null)} className="shrink-0 hover:bg-blue-100 rounded p-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}
            {fehler && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-2xl p-3 text-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{fehler}
                </div>
            )}

            {/* Erstkontakt-Hinweis (§ 7 UWG) */}
            <div className={`rounded-2xl border p-4 text-sm ${KANAL_STIL[erstkontakt.kanal]}`}>
                <p className="font-bold flex items-center gap-2">
                    {erstkontakt.kanal === 'telefon' ? <Phone className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                    Zulässiger Erstkontakt: {erstkontakt.label}
                </p>
                <p className="mt-1 text-[13px] leading-relaxed">{erstkontakt.begruendung}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Stammdaten */}
                <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-bold text-slate-700">Stammdaten</h3>
                        {bearbeiten ? (
                            <div className="flex gap-2">
                                <Button variant="ghost" onClick={() => { setBearbeiten(false); setEntwurf({}); }} disabled={speichere}>
                                    Abbrechen
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => void handleSpeichern()}
                                    disabled={speichere}
                                    icon={speichere ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                >
                                    Speichern
                                </Button>
                            </div>
                        ) : (
                            <Button variant="ghost" onClick={() => setBearbeiten(true)}>Bearbeiten</Button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {feld('Firma', 'name', <Building2 className="w-3.5 h-3.5 text-slate-300" />)}
                        <div>
                            <label htmlFor="lf-branche" className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                                Branche
                            </label>
                            {bearbeiten ? (
                                <select
                                    id="lf-branche"
                                    value={(entwurf.branche ?? lead.branche ?? '') as string}
                                    onChange={(e) => setEntwurf({ ...entwurf, branche: e.target.value })}
                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500/20"
                                >
                                    <option value="">Ohne Branche</option>
                                    {BRANCHE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            ) : (
                                <p className="text-sm text-slate-700 min-h-[38px]">{brancheLabel(lead.branche)}</p>
                            )}
                        </div>
                        {feld('Straße', 'street')}
                        {feld('PLZ', 'zip')}
                        {feld('Ort', 'city', <MapPin className="w-3.5 h-3.5 text-slate-300" />)}
                        {feld('Bundesland', 'bundesland')}
                        {feld('Website', 'website', <Globe className="w-3.5 h-3.5 text-slate-300" />)}
                        {feld('E-Mail', 'email', <Mail className="w-3.5 h-3.5 text-slate-300" />)}
                        {feld('Telefon', 'phone', <Phone className="w-3.5 h-3.5 text-slate-300" />)}
                        {feld('Ansprechpartner', 'ansprechpartner', <User className="w-3.5 h-3.5 text-slate-300" />)}
                    </div>

                    <div className="pt-2 border-t border-slate-100 text-xs text-slate-500 space-y-1">
                        <p>Quelle: <span className="font-mono">{lead.quelle}</span>
                            {lead.quelleUrl && (
                                <> · <a href={lead.quelleUrl} target="_blank" rel="noreferrer noopener" className="text-blue-600 hover:underline">Fundstelle</a></>
                            )}
                        </p>
                        {lead.scoreBegruendung && (
                            <p className="text-slate-600"><span className="font-bold">Bewertung:</span> {lead.scoreBegruendung}</p>
                        )}
                        {lead.lostReason && (
                            <p className="text-slate-600"><span className="font-bold">Absagegrund:</span> {lead.lostReason}</p>
                        )}
                    </div>
                </section>

                {/* Aktivitaeten */}
                <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
                    <h3 className="font-bold text-slate-700">Aktivitäten</h3>

                    {/* Neue Aktivitaet */}
                    <div className="bg-slate-50 rounded-xl p-3 space-y-2 border border-slate-100">
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label htmlFor="a-typ" className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Art</label>
                                <select
                                    id="a-typ"
                                    value={typ}
                                    onChange={(e) => setTyp(e.target.value as LeadActivityTyp)}
                                    className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500/20"
                                >
                                    {(Object.keys(LEAD_ACTIVITY_LABELS) as LeadActivityTyp[]).map((t) => (
                                        <option key={t} value={t}>{LEAD_ACTIVITY_LABELS[t]}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="a-faellig" className="block text-[11px] font-bold text-slate-400 uppercase mb-1">
                                    Wiedervorlage am
                                </label>
                                <input
                                    id="a-faellig"
                                    type="date"
                                    value={faelligAm}
                                    min={heuteIso()}
                                    onChange={(e) => setFaelligAm(e.target.value)}
                                    className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500/20"
                                />
                            </div>
                        </div>
                        <input
                            value={betreff}
                            onChange={(e) => setBetreff(e.target.value)}
                            placeholder="Betreff"
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                        <textarea
                            value={inhalt}
                            onChange={(e) => setInhalt(e.target.value)}
                            rows={2}
                            placeholder="Was wurde besprochen?"
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 resize-y"
                        />
                        <div className="flex justify-end">
                            <Button
                                variant="primary"
                                onClick={() => void handleAktivitaet()}
                                disabled={speichereAktivitaet || (!betreff.trim() && !inhalt.trim())}
                                icon={speichereAktivitaet ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            >
                                Eintragen
                            </Button>
                        </div>
                    </div>

                    {/* Timeline */}
                    {ladeAktivitaeten ? (
                        <p className="text-sm text-slate-400 py-6 text-center">
                            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Aktivitäten werden geladen…
                        </p>
                    ) : activities.length === 0 ? (
                        <p className="text-sm text-slate-400 py-6 text-center">Noch keine Aktivitäten erfasst.</p>
                    ) : (
                        <ol className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                            {activities.map((a) => (
                                <li key={a.id} className="border border-slate-100 rounded-xl p-3 text-sm">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <span className="text-[11px] font-bold uppercase bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">
                                                {LEAD_ACTIVITY_LABELS[a.typ] || a.typ}
                                            </span>
                                            {a.betreff && <span className="ml-2 font-bold text-slate-700">{a.betreff}</span>}
                                            {a.inhalt && <p className="text-slate-600 mt-1 whitespace-pre-wrap">{a.inhalt}</p>}
                                            <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-2">
                                                <span>{datumDe(a.createdAt)}</span>
                                                {a.faelligAm && (
                                                    <span className={`flex items-center gap-1 ${!a.erledigt && a.faelligAm <= heuteIso() ? 'text-amber-600 font-bold' : ''}`}>
                                                        <CalendarClock className="w-3 h-3" />
                                                        Wiedervorlage {datumDe(a.faelligAm)}
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                        {a.faelligAm && (
                                            <button
                                                onClick={() => void handleErledigt(a)}
                                                title={a.erledigt ? 'Wieder öffnen' : 'Als erledigt markieren'}
                                                className={`shrink-0 p-1.5 rounded-lg border transition-colors ${
                                                    a.erledigt
                                                        ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                                                        : 'border-slate-200 text-slate-300 hover:text-emerald-600 hover:border-emerald-200'
                                                }`}
                                            >
                                                <Check className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )}
                </section>
            </div>

            {/* Verloren-Dialog: Grund ist Pflicht */}
            {verlorenOffen && (
                <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in">
                        <h3 className="font-bold text-lg text-slate-800 mb-1">Lead verloren geben</h3>
                        <p className="text-sm text-slate-600 mb-3">
                            Der Grund ist Pflicht — ohne ihn lässt sich später nicht auswerten,
                            woran Angebote scheitern.
                        </p>
                        <textarea
                            value={lostReason}
                            onChange={(e) => setLostReason(e.target.value)}
                            rows={3}
                            autoFocus
                            placeholder="z. B. Preis zu hoch, koordiniert intern, kein Bedarf"
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 resize-y"
                        />
                        <div className="flex justify-end gap-3 mt-4">
                            <Button variant="ghost" onClick={() => { setVerlorenOffen(false); setLostReason(''); }}>
                                Abbrechen
                            </Button>
                            <Button variant="primary" onClick={handleVerloren} disabled={!lostReason.trim()} icon={<Ban className="w-4 h-4" />}>
                                Verloren geben
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {konvertierungOffen && darfKundeAnlegen && (
                <LeadConvertDialog
                    lead={lead}
                    onClose={() => setKonvertierungOffen(false)}
                    onConverted={(neu) => {
                        setKonvertierungOffen(false);
                        setHinweis('Kunde angelegt — der Lead steht jetzt auf „Gewonnen".');
                        onChange(neu);
                    }}
                />
            )}
        </div>
    );
};

// ─── Konvertierung ───────────────────────────────────────────────────────────

/**
 * Lead -> Kunde.
 *
 * BEWUSSTE ABWEICHUNG vom Auftrag („öffnet CustomerOnboardingWizard
 * vorbefüllt"): der CustomerOnboardingWizard ist ein MENGEN-Abgleich
 * (Projekt-Firmen + Kanzleiliste, Schritte sources → mapping → review) und
 * nimmt keine einzelne vorbefüllte Zeile entgegen. Ihn dafür umzubauen hieße,
 * einen funktionierenden Massen-Import für einen Einzelfall aufzuschneiden.
 *
 * Stattdessen dieser schlanke Dialog — mit denselben Bausteinen, die der
 * Wizard benutzt: `leadToCustomerDraft` für die Vorbelegung,
 * `findSimilarCustomers` für die Dublettenprüfung (identische Heuristik,
 * Archivierte eingeschlossen) und `createCustomer` für die Nummernvergabe über
 * die RPC. Kein Doppelstamm, kein zweiter Nummernkreis.
 */
const LeadConvertDialog: React.FC<{
    lead: Lead;
    onClose: () => void;
    onConverted: (lead: Lead) => void;
}> = ({ lead, onClose, onConverted }) => {
    const draft = useMemo(() => leadToCustomerDraft(lead), [lead]);
    const [name, setName] = useState(draft.name);
    const [dubletten, setDubletten] = useState<DuplicateHit[]>([]);
    const [pruefe, setPruefe] = useState(true);
    const [speichere, setSpeichere] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [trotzdem, setTrotzdem] = useState(false);

    useEffect(() => {
        let abgebrochen = false;
        (async () => {
            try {
                // Auch ARCHIVIERTE prüfen: sonst entsteht ein zweiter Stammsatz
                // für die Firma, die gestern archiviert wurde.
                const alle = await fetchCustomers({ includeArchived: true });
                if (abgebrochen) return;
                setDubletten(findSimilarCustomers(
                    { name: draft.name, email: draft.email, zip: draft.zip, city: draft.city },
                    alle,
                ));
            } catch (e) {
                if (!abgebrochen) setFehler(rechtFehlerOder(e));
            } finally {
                if (!abgebrochen) setPruefe(false);
            }
        })();
        return () => { abgebrochen = true; };
    }, [draft]);

    const anlegen = async () => {
        setSpeichere(true);
        setFehler(null);
        try {
            const { lead: aktualisiert } = await convertLeadToCustomer(lead, { name: name.trim() } as Partial<Customer>);
            onConverted(aktualisiert);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
            setSpeichere(false);
        }
    };

    const blockiert = dubletten.length > 0 && !trotzdem;

    return (
        <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden animate-scale-in">
                <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-500/20 p-2 rounded-xl border border-blue-500/30">
                            <UserPlus className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <h3 className="font-bold">In Kunde umwandeln</h3>
                            <p className="text-xs text-slate-400">Stammdaten aus dem Lead, Kundennummer automatisch.</p>
                        </div>
                    </div>
                    <button onClick={onClose} disabled={speichere} className="hover:bg-white/10 p-2 rounded-full disabled:opacity-40">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    <div>
                        <label htmlFor="k-name" className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                            Name / Firma *
                        </label>
                        <input
                            id="k-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                    </div>

                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm bg-slate-50 rounded-xl p-4 border border-slate-100">
                        {([
                            ['Straße', draft.street],
                            ['PLZ / Ort', [draft.zip, draft.city].filter(Boolean).join(' ')],
                            ['E-Mail', draft.email],
                            ['Telefon', draft.phone],
                            ['Website', draft.website],
                            ['Branche', draft.branche ? brancheLabel(draft.branche) : ''],
                        ] as [string, string | undefined][]).map(([label, wert]) => (
                            <div key={label}>
                                <dt className="text-[11px] font-bold text-slate-400 uppercase">{label}</dt>
                                <dd className="text-slate-700">{wert || <span className="text-slate-300">—</span>}</dd>
                            </div>
                        ))}
                    </dl>

                    {pruefe ? (
                        <p className="text-sm text-slate-500 flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />Dublettenprüfung läuft…
                        </p>
                    ) : dubletten.length > 0 ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
                            <p className="font-bold text-amber-800 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                {dubletten.length === 1 ? 'Ein ähnlicher Kunde existiert bereits' : `${dubletten.length} ähnliche Kunden existieren bereits`}
                            </p>
                            <ul className="mt-2 space-y-1 text-amber-900">
                                {dubletten.slice(0, 5).map((h) => (
                                    <li key={h.customer.id}>
                                        <span className="font-bold">{h.customer.name}</span>
                                        {h.customer.customerNumber && <span className="font-mono text-xs"> ({h.customer.customerNumber})</span>}
                                        <span className="text-xs"> — {h.reasons.join(', ')}</span>
                                    </li>
                                ))}
                            </ul>
                            <label className="flex items-center gap-2 mt-3 text-amber-900 cursor-pointer select-none">
                                <input type="checkbox" checked={trotzdem} onChange={(e) => setTrotzdem(e.target.checked)} className="w-4 h-4" />
                                Trotzdem als neuen Kunden anlegen
                            </label>
                        </div>
                    ) : (
                        <p className="text-sm text-emerald-700 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" />Kein ähnlicher Kunde im Bestand.
                        </p>
                    )}

                    {fehler && <p className="text-sm text-red-600">{fehler}</p>}
                </div>

                <div className="p-4 bg-slate-50 border-t flex justify-between items-center shrink-0">
                    <span className="text-xs text-slate-500">Der Lead wird danach auf „Gewonnen" gesetzt.</span>
                    <div className="flex gap-3">
                        <Button variant="ghost" onClick={onClose} disabled={speichere}>Abbrechen</Button>
                        <Button
                            variant="primary"
                            onClick={() => void anlegen()}
                            disabled={speichere || pruefe || blockiert || !name.trim()}
                            icon={speichere ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                        >
                            Kunde anlegen
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
